/**
 * Field-level controls for the Pipeline Report filter panel. Previously these
 * rendered inline in a cramped second header row (one per column); they now live
 * inside PipelineReportFilterBar's popover, where a range field has room to lay
 * its two inputs side by side instead of being squeezed into a column's width.
 */

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { SelectOption } from "@/lib/option-search";

/** Sentinels for the pick-list filter `<Select>`s — Radix Select rejects an empty-string value. */
export const FILTER_ALL = "__all__";
export const FILTER_NONE = "__none__";

/** A labeled field within the filter panel's grid. */
export function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </div>
      {children}
    </div>
  );
}

export function FilterText({
  value,
  onChange,
  placeholder = "Filter…",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-9 text-sm"
    />
  );
}

export function FilterSelect({
  value,
  onChange,
  options,
  includeNone = true,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  includeNone?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full text-sm">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={FILTER_ALL}>All</SelectItem>
        {includeNone && <SelectItem value={FILTER_NONE}>— None —</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function FilterNumberRange({
  min,
  max,
  onMinChange,
  onMaxChange,
}: {
  min: string;
  max: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        value={min}
        onChange={(e) => onMinChange(e.target.value)}
        placeholder="Min"
        className="h-9 w-full text-sm"
      />
      <span className="text-sm text-muted-foreground">to</span>
      <Input
        type="number"
        value={max}
        onChange={(e) => onMaxChange(e.target.value)}
        placeholder="Max"
        className="h-9 w-full text-sm"
      />
    </div>
  );
}

export function FilterDateRange({
  from,
  to,
  onFromChange,
  onToChange,
}: {
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        type="date"
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        className="h-9 w-full text-sm"
      />
      <span className="text-sm text-muted-foreground">to</span>
      <Input
        type="date"
        value={to}
        onChange={(e) => onToChange(e.target.value)}
        className="h-9 w-full text-sm"
      />
    </div>
  );
}
